import { EventsCalendarFilterRange, EventsCalenderFilterEventType } from "components/investorrelations/enums";
import { parseAsDate } from "components/ux/urlParsers";
import addMonths from "date-fns/addMonths";
import subMonths from "date-fns/subMonths";
import { createParser, parseAsBoolean, parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from "nuqs";
import { IIssuerType, IOrderDirection } from "types/graphqlTypes";

type EventsCalendarState = {
  period?: EventsCalendarFilterRange;
};

export default function useEventsCalendarState(initialState?: EventsCalendarState) {
  const currentDate = new Date();

  const [myPositions, setMyPositions] = useQueryState("myPositions", parseAsBoolean.withDefault(false));
  const [isInSP500, setIsInSP500] = useQueryState("isInSP500", parseAsBoolean.withDefault(false));
  const [isInBookmarks, setIsInBookmarks] = useQueryState("isInBookmarks", parseAsBoolean.withDefault(false));
  const [searchQuery, setSearchQuery] = useQueryState("searchQuery", parseAsString);
  const [period, setPeriod] = useQueryState(
    "period",
    periodSchema.withDefault(initialState?.period || EventsCalendarFilterRange.Today),
  );
  const [orderBy, setOrderBy] = useQueryState("orderBy", oerderBySchema.withDefault(IOrderDirection.Ascending));
  const [calendarInterval, setCalendarInterval] = useQueryStates({
    startDate: parseAsDate.withDefault(
      initialState?.period === EventsCalendarFilterRange.InTheYear ? subMonths(currentDate, 6) : currentDate,
    ),
    endDate: parseAsDate.withDefault(
      initialState?.period === EventsCalendarFilterRange.InTheYear ? addMonths(currentDate, 6) : currentDate,
    ),
  });
  const [eventType, setEventType] = useQueryState(
    "eventType",
    parseAsStringEnum<EventsCalenderFilterEventType>(Object.values(EventsCalenderFilterEventType)),
  );
  const [issuerType, setIssuerType] = useQueryState(
    "issuerType",
    issuerTypeSchema.withDefault(IIssuerType.BrazilianCompany),
  );

  return {
    myPositions,
    isInSP500,
    isInBookmarks,
    searchQuery,
    eventType,
    issuerType,
    period,
    orderBy,
    calendarInterval,

    setMyPositions,
    setIsInSP500,
    setIsInBookmarks,
    setSearchQuery,
    setEventType,
    setIssuerType,
    setPeriod,
    setOrderBy,
    setCalendarInterval,
  };
}

const issuerTypeSchema = createParser<IIssuerType | null>({
  parse(query) {
    const issuerType = query.toUpperCase();
    const possibleTypes = [IIssuerType.AmericanCompany, IIssuerType.AmericanReit, IIssuerType.BrazilianCompany];

    return possibleTypes.includes(issuerType as IIssuerType) ? (issuerType as IIssuerType) : null;
  },
  serialize(issuerType) {
    return (issuerType || "").toLowerCase();
  },
});

const periodSchema = createParser<EventsCalendarFilterRange | null>({
  parse(query) {
    const period = query.toUpperCase();
    const possibleTypes = [
      EventsCalendarFilterRange.Today,
      EventsCalendarFilterRange.InTheWeek,
      EventsCalendarFilterRange.InTheMonth,
      EventsCalendarFilterRange.InTheNextMonth,
      EventsCalendarFilterRange.InTheLastMonth,
      EventsCalendarFilterRange.InTheNextNinetyDays,
      EventsCalendarFilterRange.InTheYear,
      EventsCalendarFilterRange.InSelectedPeriod,
    ];

    return possibleTypes.includes(period as EventsCalendarFilterRange) ? (period as EventsCalendarFilterRange) : null;
  },
  serialize(period) {
    return (period || "").toLowerCase();
  },
});

const oerderBySchema = createParser<IOrderDirection | null>({
  parse(query) {
    const oderBy = query.toUpperCase();
    const possibleTypes = [IOrderDirection.Ascending, IOrderDirection.Descending];

    return possibleTypes.includes(oderBy as IOrderDirection) ? (oderBy as IOrderDirection) : null;
  },
  serialize(oderBy) {
    return (oderBy || "").toLowerCase();
  },
});
